import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Save, Rocket, Users, Clock, Sparkles, Ticket } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";
import { gbp, fmtDateTime, timeParts } from "../../lib/format";

const Label = ({ children }) => <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{children}</label>;
const Input = (p) => <input className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]" {...p} />;

const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" role="switch" aria-checked={checked} data-testid={testid} onClick={() => onChange(!checked)}
    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${checked ? "bg-[var(--neon-green)] shadow-[0_0_14px_rgba(0,255,163,.5)]" : "bg-white/15"}`}>
    <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-transform duration-200 ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

export default function AdminReferrals() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [comps, setComps] = useState([]);

  // Standard programme
  const [enabled, setEnabled] = useState(true);
  const [reward, setReward] = useState(5);
  const [friendReward, setFriendReward] = useState(5);
  const [discountMin, setDiscountMin] = useState(10);
  const [goldenMax, setGoldenMax] = useState(1);
  const [minSpend, setMinSpend] = useState(10);
  // Golden ticket grant
  const [grantEmail, setGrantEmail] = useState("");
  const [grantQty, setGrantQty] = useState(1);
  const [granting, setGranting] = useState(false);
  // Launch promo
  const [launchEnabled, setLaunchEnabled] = useState(false);
  const [launchCompId, setLaunchCompId] = useState("");
  const [launchFreeTickets, setLaunchFreeTickets] = useState(2);
  const [launchEndsAt, setLaunchEndsAt] = useState(null);

  const hydrate = (s) => {
    setEnabled(s.referral_enabled !== false);
    setReward(s.referral_reward ?? 5);
    setFriendReward(s.referral_friend_discount ?? s.referral_friend_reward ?? 5);
    setDiscountMin(s.referral_friend_discount_min ?? s.referral_min_spend ?? 10);
    setGoldenMax(s.golden_ticket_max ?? 1);
    setMinSpend(s.referral_min_spend ?? 10);
    setLaunchEnabled(!!s.referral_launch_enabled);
    setLaunchCompId(s.referral_launch_competition_id || "");
    setLaunchFreeTickets(s.referral_launch_free_tickets ?? 2);
    setLaunchEndsAt(s.referral_launch_ends_at || null);
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/settings").then((r) => r.data).catch(() => ({})),
      api.get("/competitions").then((r) => r.data).catch(() => []),
    ]).then(([s, c]) => {
      hydrate(s?.settings ? s.settings : s || {});
      setComps(Array.isArray(c) ? c : []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const saveStandard = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        referral_enabled: enabled,
        referral_reward: Number(reward) || 0,
        referral_friend_discount: Number(friendReward) || 0,
        referral_friend_discount_min: Number(discountMin) || 0,
        golden_ticket_max: Number(goldenMax) || 0,
        referral_min_spend: Number(minSpend) || 0,
      });
      toast.success("Refer-a-Friend settings saved.");
      load();
    } catch (e) { toast.error(getErr(e)); } finally { setSaving(false); }
  };

  // Starts a fresh 30-day launch window and enables the promo (server sets referral_launch_ends_at).
  const startLaunch = async () => {
    if (!launchCompId) { toast.error("Pick the competition friends get free tickets to."); return; }
    if (!window.confirm("Start the 30-day launch referral promo now? Both the referrer and the friend get free tickets once the friend verifies.")) return;
    setLaunching(true);
    try {
      await api.put("/admin/settings", {
        referral_launch_enabled: true,
        referral_launch_competition_id: launchCompId,
        referral_launch_free_tickets: Number(launchFreeTickets) || 1,
        referral_launch_start: true,
      });
      toast.success("Launch referral promo is LIVE — the gold banner is now showing to users.");
      load();
    } catch (e) { toast.error(getErr(e)); } finally { setLaunching(false); }
  };

  const grantGolden = async () => {
    if (!grantEmail.trim()) { toast.error("Enter the member's email."); return; }
    setGranting(true);
    try {
      const { data } = await api.post("/admin/golden-tickets/grant", {
        user_email: grantEmail.trim(),
        quantity: Number(grantQty) || 1,
        max_ticket_value: Number(goldenMax) || undefined,
      });
      toast.success(`Granted ${data?.granted ?? grantQty} Golden Ticket(s) to ${grantEmail.trim()}.`);
      setGrantEmail(""); setGrantQty(1);
    } catch (e) { toast.error(getErr(e, "Could not grant Golden Ticket (backend may not be live yet)")); }
    finally { setGranting(false); }
  };

  const stopLaunch = async () => {
    setLaunching(true);
    try {
      await api.put("/admin/settings", { referral_launch_enabled: false });
      toast.success("Launch promo turned off.");
      load();
    } catch (e) { toast.error(getErr(e)); } finally { setLaunching(false); }
  };

  if (loading) return <Spinner />;
  const lt = launchEndsAt ? timeParts(launchEndsAt) : null;
  const launchLive = launchEnabled && lt && !lt.done;

  return (
    <div data-testid="admin-referrals" className="max-w-3xl space-y-5">
      <div className="flex items-center gap-2">
        <Gift className="h-6 w-6 text-[var(--neon-gold)]" />
        <h2 className="font-display uppercase text-2xl text-white">Refer a Friend</h2>
      </div>

      {/* Launch promo */}
      <NeonCard variant="flow" innerClassName="p-6 border border-[rgba(255,210,74,.35)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Rocket className="h-5 w-5 text-[var(--neon-gold)]" /><p className="font-display uppercase text-lg text-white">Launch Promo (free tickets each)</p></div>
          {launchLive ? <Pill color="green">LIVE</Pill> : <Pill color="grey">Off</Pill>}
        </div>
        <p className="mt-1 text-sm text-[var(--text-muted)]">Time-limited 30-day promo: the referrer AND the friend each get free tickets to a chosen competition once the friend verifies email + phone. Runs independently of the standard £ programme.</p>

        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Competition (free tickets to)</Label>
            <select value={launchCompId} onChange={(e) => setLaunchCompId(e.target.value)} data-testid="referral-launch-comp" className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm">
              <option value="">— Select a competition —</option>
              {comps.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
          <div><Label>Free tickets per person</Label><Input type="number" min="1" value={launchFreeTickets} onChange={(e) => setLaunchFreeTickets(e.target.value)} data-testid="referral-launch-tickets" /></div>
        </div>

        {launchLive && (
          <div className="mt-3 flex items-center gap-2 text-sm text-[var(--neon-gold)]" data-testid="referral-launch-status">
            <Clock className="h-4 w-4" /> Ends {fmtDateTime(launchEndsAt)} · {lt.d}d {lt.h}h left
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-3">
          <NeonButton variant="pink" onClick={startLaunch} loading={launching} data-testid="referral-launch-start">
            <Rocket className="h-4 w-4" /> {launchLive ? "Restart 30-day promo" : "Start 30-day promo"}
          </NeonButton>
          {launchLive && <NeonButton variant="ghost" onClick={stopLaunch} loading={launching} data-testid="referral-launch-stop">Turn off</NeonButton>}
        </div>
      </NeonCard>

      {/* Standard programme */}
      <NeonCard variant="cyan" innerClassName="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Users className="h-5 w-5 text-[var(--neon-cyan)]" /><p className="font-display uppercase text-lg text-white">Standard Programme (£ rewards)</p></div>
          <Toggle checked={enabled} onChange={setEnabled} testid="referral-enabled" />
        </div>
        <p className="mt-1 text-sm text-[var(--text-muted)]">When on: the referrer gets site credit once the friend deposits &amp; wagers the qualifying amount; the friend gets a one-time <span className="text-white">£ discount at checkout</span> on their first basket at/above the discount minimum; and <span className="text-[var(--neon-gold)]">both get a Golden Ticket</span> (free entry to any competition with tickets up to the Golden Ticket cap). Switching OFF hides the whole card from users.</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          <div><Label>Referrer credit £</Label><Input type="number" step="0.01" value={reward} onChange={(e) => setReward(e.target.value)} data-testid="referral-reward" /></div>
          <div><Label>Qualifying wager £</Label><Input type="number" step="0.01" value={minSpend} onChange={(e) => setMinSpend(e.target.value)} data-testid="referral-min-spend" /></div>
          <div><Label>Friend checkout discount £</Label><Input type="number" step="0.01" value={friendReward} onChange={(e) => setFriendReward(e.target.value)} data-testid="referral-friend-reward" /></div>
          <div><Label>Discount unlocks at basket £</Label><Input type="number" step="0.01" value={discountMin} onChange={(e) => setDiscountMin(e.target.value)} data-testid="referral-discount-min" /></div>
          <div><Label>Golden Ticket max value £</Label><Input type="number" step="0.01" value={goldenMax} onChange={(e) => setGoldenMax(e.target.value)} data-testid="referral-golden-max" /></div>
        </div>
        <div className="mt-3 flex items-start gap-2 text-xs text-[var(--text-muted)]">
          <Sparkles className="h-3.5 w-3.5 mt-0.5 text-[var(--neon-cyan)] shrink-0" /> <span>Preview: friend's basket ≥ {gbp(discountMin)} → {gbp(friendReward)} off at checkout; referrer gets {gbp(reward)} credit once the friend wagers {gbp(minSpend)}. Both also receive a Golden Ticket (free entry to competitions with tickets ≤ {gbp(goldenMax)}).</span>
        </div>
        <NeonButton variant="outlineCyan" className="mt-4" onClick={saveStandard} loading={saving} data-testid="referral-save"><Save className="h-4 w-4" /> Save programme</NeonButton>
      </NeonCard>

      {/* Grant Golden Ticket */}
      <NeonCard variant="flow" innerClassName="p-6 border border-[rgba(255,210,74,.35)]">
        <div className="flex items-center gap-2"><Ticket className="h-5 w-5 text-[var(--neon-gold)]" /><p className="font-display uppercase text-lg text-white">Grant a Golden Ticket</p></div>
        <p className="mt-1 text-sm text-[var(--text-muted)]">Hand a member one or more Golden Tickets as a goodwill / win-back gesture (free entry to any competition with tickets up to {gbp(goldenMax)}).</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-4 items-end">
          <div className="sm:col-span-2"><Label>Member email</Label><Input type="email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="member@email.com" data-testid="grant-email" /></div>
          <div><Label>Quantity</Label><Input type="number" min="1" max="20" value={grantQty} onChange={(e) => setGrantQty(e.target.value)} data-testid="grant-qty" /></div>
        </div>
        <NeonButton variant="pink" className="mt-4" onClick={grantGolden} loading={granting} data-testid="grant-golden"><Ticket className="h-4 w-4" /> Grant Golden Ticket</NeonButton>
      </NeonCard>
    </div>
  );
}
